import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { usePostHog } from 'posthog-js/react';
import { useToast } from '@/hooks/useToast';
import { setCookie } from '@/lib/utils';
import { getUserProfileFromToken } from '@/lib/auth';
import { useUserProfile } from '@/contexts/user.context';
import { registerUserAccount, loginUserAccount } from '@/lib/apis';
import type { EmailSignupData, EmailSigninData } from '@/lib/validations/auth';

export const useEmailSignup = () => {
  const { setUser } = useUserProfile();
  const { toast } = useToast();
  const posthog = usePostHog();
  const navigate = useNavigate();

  const signupMutation = useMutation({
    mutationFn: async (data: EmailSignupData) => {
      return registerUserAccount(data.name, data.email, data.password);
    },
    onSuccess: async (response) => {
      try {
        if (response?.token) {
          setCookie('access_token', response.token, 1);
          const userProfile = getUserProfileFromToken(response.token);
          setUser(userProfile);
          const { name, email } = userProfile;
          
          posthog?.identify(email, {
            email,
            name,
          });
          posthog?.capture('registration_completed', { method: 'email' });

          // Just navigate to authenticated area - _authenticated.tsx will handle the rest
          navigate({ to: '/campaigns' });
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unable to complete signup. Please try again.';
        toast({
          title: 'Signup Failed',
          description: errorMessage,
        });
        throw error;
      }
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'Unable to complete signup. Please try again.';
      toast({
        title: 'Signup Failed',
        description: errorMessage,
      });
    }
  });

  return {
    signup: signupMutation.mutate,
    isLoading: signupMutation.isPending
  };
};

export const useEmailSignin = () => {
  const { setUser } = useUserProfile();
  const { toast } = useToast();
  const posthog = usePostHog();
  const navigate = useNavigate();

  const signinMutation = useMutation({
    mutationFn: (data: EmailSigninData) => {
      return loginUserAccount(data.email, data.password);
    },
    onSuccess: async (response) => {
      try {
        if (response?.token) {
          setCookie('access_token', response.token, 1);
          const userProfile = getUserProfileFromToken(response.token);
          setUser(userProfile);
          const { name, email } = userProfile;
          
          posthog?.identify(email, {
            email,
            name,
          });
          posthog?.capture('login_completed', { method: 'email' });

          // Just navigate to authenticated area - _authenticated.tsx will handle the rest
          navigate({ to: '/campaigns' });
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unable to sign in. Please try again.';
        toast({
          title: 'Sign In Failed',
          description: errorMessage,
        });
        throw error;
      }
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'Unable to sign in. Please try again.';
      toast({
        title: 'Sign In Failed',
        description: errorMessage,
      });
    }
  });

  return {
    signin: signinMutation.mutate,
    isLoading: signinMutation.isPending
  };
};